import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../config/axios';
import NavbarEvaluator from './NavbarEvaluator';
import toast from 'react-hot-toast';

const EvaluatorDashboard = () => {
  const { evaluatorToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]); // [{ evaluation, survey }]
  const [refreshing, setRefreshing] = useState(false);
  // Permitir usar íconos remotos vía variables de entorno o fallback local
  const PDF_ICON_URL = process.env.REACT_APP_PDF_ICON_URL || (process.env.PUBLIC_URL + '/icons/pdf.png');

  // Decodificar JWT (sin verificar) para obtener el DNI del evaluador
  const evaluatorDni = useMemo(() => {
    try {
      if (!evaluatorToken) return null;
      const base64Url = evaluatorToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      return payload?.dni ? String(payload.dni) : null;
    } catch (e) {
      return null;
    }
  }, [evaluatorToken]);

  const evaluator = useMemo(() => {
    if (!items || items.length === 0) return null;
    const anyEval = items[0].evaluation;
    return (anyEval.evaluators || []).find(e => String(e.dni) === String(evaluatorDni));
  }, [items, evaluatorDni]);

  const loadItems = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      const res = await api.get(`/api/performance-evaluation/by-evaluator/${evaluatorToken}`);
      setItems(res.data.items || []);
    } catch (e) {
      // silencioso para no spamear
    } finally {
      if (showRefreshing) setRefreshing(false);
    }
  }, [evaluatorToken]);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        await loadItems();
      } catch (e) {
        toast.error('No se pudo cargar la información');
      } finally {
        setLoading(false);
      }
    };
    if (evaluatorToken) init();
  }, [evaluatorToken, loadItems]);

  // Actualización periódica (casi en tiempo real)
  useEffect(() => {
    const id = setInterval(() => {
      loadItems();
    }, 10000);
    const onVisibility = () => {
      if (!document.hidden) loadItems();
    };
    document.addEventListener('visibilitychange', onVisibility);
    // Realtime por BroadcastChannel (cuando el tab de evaluación envía mensaje)
    let bc;
    if ('BroadcastChannel' in window) {
      bc = new BroadcastChannel('evaluation-updates');
      bc.onmessage = (event) => {
        // Si es un cambio de estado específico, refrescar inmediatamente
        if (event.data && event.data.type === 'status-changed') {
          loadItems();
        } else {
          // Para otros mensajes, también refrescar
          loadItems();
        }
      };
    }
    // Fallback via localStorage event
    const handleStorage = (e) => {
      if (e.key === 'evaluation-updated') {
        // Refrescar inmediatamente cuando se detecta un cambio
        loadItems();
      }
    };
    window.addEventListener('storage', handleStorage);
    
    // También escuchar eventos de focus para refrescar cuando se regresa a la pestaña
    const handleFocus = () => {
      loadItems();
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      if (bc) bc.close();
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadItems]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#19B394' }}>Cargando...</div>;
  }

  if (!evaluator) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#dc3545' }}>Token inválido o evaluador no encontrado.</div>;
  }

  const handleOpenEvaluation = (surveyId, pairToken) => {
    const url = `/performance-form/${surveyId}/${pairToken}`;
    window.open(url, '_blank');
  };

  const handleDownloadPDF = async (surveyId, pairToken, evaluateeName) => {
    try {
      const response = await api.get(`/api/performance-evaluation/download-pdf/${surveyId}/${pairToken}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evaluacion_${evaluateeName}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#eefdfa' }}>
      {/* Navbar consistente con la app */}
      <NavbarEvaluator evaluatorName={evaluator.name} />
      <div className="container with-navbar" style={{ paddingTop: '70px', paddingBottom: '70px' }}>
        {/* Encabezado de bienvenida simple bajo el navbar */}
        
        {/* Indicador de refresco */}
        {refreshing && (
          <div style={{ 
            textAlign: 'center', 
            margin: '20px auto',
            maxWidth: '400px'
          }}>
            <div style={{ 
              padding: '10px', 
              color: '#19B394', 
              fontSize: '14px',
              background: '#f0fdfa',
              borderRadius: '8px',
              border: '1px solid #19B394'
            }}>
              🔄 Actualizando estados...
            </div>
          </div>
        )}
        
      {items.map(({ evaluation, survey }, blockIdx) => {
        const pairs = (evaluation.evaluations || [])
          .filter(p => String(p.evaluatorId) === String(evaluator.dni));
        const evaluateesMap = new Map((evaluation.evaluatees || []).map(e => [String(e.dni), e]));
        return (
          <div key={blockIdx} style={{
            background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(25,179,148,0.10)', padding: 32, maxWidth: 1000,
            margin: '0 auto', marginTop: 40
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ color: '#19B394', margin: 0 }}>{survey?.title || 'Evaluaciones de Desempeño'}</h2>
              <div />
            </div>
            <div style={{ marginTop: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', color: '#19B394', fontWeight: 600 , width: '40%' }}>Evaluado</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', color: '#19B394', fontWeight: 600 , width: '20%' }}>DNI</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', color: '#19B394', fontWeight: 600 , width: '20%'}}>Estado</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', color: '#19B394', fontWeight: 600 , width: '20%'}}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pairs.map((p, idx) => {
                    const e = evaluateesMap.get(String(p.evaluateeId));
                    const estado = p.status === 'completed' ? 'Completado' : (p.status === 'in_progress' ? 'En curso' : 'Pendiente');
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f3f4' }}>
                        <td style={{ padding: '12px 8px' }}>{e?.name}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>{e?.dni}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center', color: estado === 'Completado' ? '#19B394' : (estado === 'En curso' ? '#007bff' : '#dc3545'), fontWeight: 600 }}>{estado}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          {estado === 'Completado' ? (
                            <div className="eval-actions-row">
                              <button
                                onClick={() => handleDownloadPDF(evaluation.surveyId, p.token, e?.name)}
                                className="download-btn"
                                title="Descargar PDF"
                                aria-label="Descargar PDF"
                              >
                                <img src={PDF_ICON_URL} alt="PDF" className="download-icon" referrerPolicy="no-referrer" onError={(e)=>{ e.currentTarget.src = process.env.PUBLIC_URL + '/icons/pdf.svg'; }} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleOpenEvaluation(evaluation.surveyId, p.token)}
                              style={{
                                background: '#19B394', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12
                              }}
                            >
                              Evaluar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default EvaluatorDashboard;


